import {
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    IconButton,
} from '@material-ui/core';
import React, {useContext, useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import clsx from 'clsx';
import {PBold} from 'components/TypographyShortcuts';
import {FormState} from './context';
import {DeleteForever} from '@material-ui/icons';
import {ConfirmPrompt} from './Prompt';
import {PSingleLine} from 'components/TypographyShortcuts';
import {dateFormatter, numberDaysBetween} from 'components/forms/locale/utils';

const useStyles = makeStyles(theme => ({
    root: {
        minWidth: '330px',
        border: '0.5px solid lightgray',
    },
    content: {
        padding: '1rem',
        '&:last-child': {
            paddingBottom: '1rem', // sadly this is necessary
        },
    },
    shaded: {
        backgroundColor: theme.palette.gray.light2,
    },
}));

const SSLPosition = ({position, namedPosition}) => {
    return [
        <Grid item xs={12} key={1}>
            <Box textAlign='right'>
                {dateFormatter(position.date_reported)} -{' '}
                {dateFormatter(position.date_detached)}
            </Box>
        </Grid>,
        <Grid item xs={12} container key={2}>
            <Grid item xs={5}>
                <b>{namedPosition}</b>
            </Grid>
            <Grid item xs={7}>
                <Box textAlign='right'>
                    {position.place_reported} - {position.place_detached}
                </Box>
            </Grid>
        </Grid>,
        <Grid item xs={12} container key={3}>
            <Grid item xs>
                {position.watertype ? (
                    <PBold>{position.watertype}</PBold>
                ) : (
                    <PBold style={{color: 'red'}}>Watertype is missing.</PBold>
                )}
            </Grid>
            <Grid item xs>
                <Box textAlign='right'>
                    <b>Sea Days: {position.total_number_days}</b>
                </Box>
            </Grid>
        </Grid>,
    ];
};

const NOAAPosition = ({position, namedPosition}) => {
    const numberDaysCredit = numberDaysBetween(
        position.date_reported,
        position.date_detached
    );
    return [
        <Grid item container xs={12} justifyContent='space-between'>
            <Grid item>
                <PSingleLine>{namedPosition}</PSingleLine>
            </Grid>
            <Grid item>
                <PSingleLine align='right'>
                    {dateFormatter(position.date_reported)} -{' '}
                    {dateFormatter(position.date_detached)}
                </PSingleLine>
            </Grid>
        </Grid>,
        <Grid item xs={12} container justifyContent='space-between'>
            <Grid item>
                {position.watertype ? (
                    <PSingleLine>{position.watertype}</PSingleLine>
                ) : null}
            </Grid>
            <Grid item>
                <PSingleLine align='right'>
                    Days Assigned: {numberDaysCredit}
                </PSingleLine>
            </Grid>
        </Grid>,
        <Grid item xs={12} container justifyContent='space-between'>
            <Grid item xs={12}>
                <PSingleLine align='right'>
                    USCG Sea Days Credit:{' '}
                    {Math.round(numberDaysCredit * 6) / 10}
                </PSingleLine>
            </Grid>
        </Grid>,
    ];
};

const USCGPosition = ({position, namedPosition}) => {
    const numberDaysCredit = numberDaysBetween(
        position.date_reported,
        position.date_detached
    );
    return [
        <Grid item container xs={12} justifyContent='space-between'>
            <Grid item>
                <PSingleLine>{namedPosition}</PSingleLine>
            </Grid>
            <Grid item>
                <PSingleLine align='right'>
                    {dateFormatter(position.date_reported)} -{' '}
                    {dateFormatter(position.date_detached)}
                </PSingleLine>
            </Grid>
        </Grid>,
        <Grid item xs={12} container justifyContent='space-between'>
            <Grid item>
                {position.watertype ? (
                    <PSingleLine>{position.watertype}</PSingleLine>
                ) : null}
            </Grid>
            <Grid item>
                <PSingleLine align='right'>
                    Days Assigned: {numberDaysCredit}
                </PSingleLine>
            </Grid>
        </Grid>,
        <Grid item xs={12} container justifyContent='space-between'>
            <Grid item xs={12}>
                <PSingleLine align='right'>
                    USCG Sea Days Credit:{' '}
                    {Math.round(numberDaysCredit * 6) / 10}
                </PSingleLine>
            </Grid>
        </Grid>,
    ];
};

const PositionCard = ({position, shaded = false, sslType, children}) => {
    const classes = useStyles();

    const namedPosition =
        position.deck_position_served ||
        position.engine_position_served ||
        position.offshore_installation_deck_position_served ||
        position.offshore_installation_engine_position_served ||
        position.offshore_installation_drilling_position_served ||
        position.offshore_installation_supportstaff_position_served;

    const ShowPosition =
        sslType === 'ssl'
            ? SSLPosition
            : sslType === 'noaatoss'
            ? NOAAPosition
            : sslType === 'armytoss'
            ? USCGPosition
            : sslType === 'uscgtoss'
            ? USCGPosition
            : SSLPosition;

    return (
        <Card className={clsx(classes.root, {[classes.shaded]: shaded})}>
            <CardContent className={classes.content}>
                <Grid container spacing={1}>
                    <ShowPosition
                        position={position}
                        namedPosition={namedPosition}
                    />
                    <Grid item xs={12}>
                        {children}
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
};

const PositionCardForm = ({
    position,
    index,
    parentLoopName,
    parentIndex,
    ...rest
}) => {
    const [showConfirmPrompt, setShowConfirmPrompt] = useState(false);
    const {navigateToNamedLoop, deleteNamedLoop} = useContext(FormState);

    const editLoop = () => {
        navigateToNamedLoop(
            'service_entries',
            index,
            parentLoopName,
            parentIndex
        );
    };

    const openConfirmPrompt = () => {
        setShowConfirmPrompt(true);
    };

    const hideConfirmPrompt = () => {
        setShowConfirmPrompt(false);
    };

    const deleteLoop = () => {
        deleteNamedLoop('service_entries', index, parentLoopName, parentIndex);
        hideConfirmPrompt();
    };

    const namedPosition =
        position.deck_position_served ||
        position.engine_position_served ||
        position.offshore_installation_deck_position_served ||
        position.offshore_installation_engine_position_served ||
        position.offshore_installation_drilling_position_served ||
        position.offshore_installation_supportstaff_position_served;

    return (
        <PositionCard position={position} {...rest}>
            <Grid
                item
                container
                alignItems='center'
                style={{marginTop: '.5em'}}
            >
                <Grid item style={{flexGrow: '1'}}>
                    <Button
                        fullWidth
                        color='primary'
                        variant='contained'
                        onClick={editLoop}
                    >
                        Edit {namedPosition}
                    </Button>
                </Grid>
                <Grid item>
                    <IconButton
                        color='secondary'
                        variant='contained'
                        onClick={openConfirmPrompt}
                        aria-label={`Delete ${namedPosition}`}
                    >
                        <DeleteForever />
                    </IconButton>
                </Grid>
            </Grid>
            <ConfirmPrompt
                open={showConfirmPrompt}
                description={`Are you sure you want to delete ${namedPosition}?`}
                onConfirm={deleteLoop}
                onCancel={hideConfirmPrompt}
            />
        </PositionCard>
    );
};

export default PositionCard;
export {PositionCard, PositionCardForm};
